<template>
  <b-modal
    v-model="modalVisible"
    id="creative-preview-modal"
    title="Creative Preview"
    size="lg"
    hide-footer
  >
    <div v-if="previewItem">
      <!-- Preview Content -->
      <div class="text-center mb-3">
        <div class="creative-preview-container">
          <!-- Text-based content (AD_TEXT, LANDING_PAGE_URL) -->
          <div
            v-if="isTextMaterial(previewItem)"
            class="text-content-preview p-4"
            style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); min-height: 200px; border-radius: 8px; border: 1px solid #dee2e6;"
          >
            <feather-icon
              :icon="getTextMaterialIcon(previewItem)"
              size="48"
              class="text-primary mb-3"
            />
            <h5 class="mb-3">
              {{ getTextMaterialTitle(previewItem) }}
            </h5>
            <div class="text-content-box p-3 bg-white rounded">
              <p
class="mb-0 text-dark"
style="font-size: 1.1rem; word-wrap: break-word;"
>
                {{ getTextMaterialContent(previewItem) }}
              </p>
            </div>
          </div>
          <!-- Image content -->
          <b-img
            v-else-if="!isVideoMaterial(previewItem) && getCreativeThumbnailUrl(previewItem)"
            :src="getCreativeThumbnailUrl(previewItem)"
            :alt="`Creative ${previewItem.creative_id}`"
            fluid
            class="creative-preview-image"
            style="max-height: 400px; border-radius: 8px;"
            @error="handleImageError($event, previewItem)"
          />
          <div
            v-else-if="!isVideoMaterial(previewItem) && !isTextMaterial(previewItem)"
            class="preview-placeholder d-flex flex-column align-items-center justify-content-center"
            style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); min-height: 300px; border-radius: 8px; border: 2px dashed #ced4da;"
          >
            <feather-icon
              icon="ImageIcon"
              size="48"
              class="text-muted mb-3"
            />
            <h5 class="text-muted">
              Creative {{ previewItem.creative_id }}
            </h5>
            <p class="text-muted small">
              Image preview not available
            </p>
            <p
              v-if="previewItem.creative_id && /^\d+$/.test(previewItem.creative_id)"
              class="text-info small"
            >
              TikTok Creative ID: {{ previewItem.creative_id }}
            </p>
          </div>
          <!-- Video content -->
          <div
            v-else
            class="video-preview-container"
            style="background: #f8f9fa; padding: 2rem; border-radius: 8px;"
          >
            <div
              v-if="getVideoUrl(previewItem)"
              class="mt-3"
            >
              <video
                controls
                style="max-width: 100%; max-height: 400px; width: 100%;"
                :src="getVideoUrl(previewItem)"
              >
                Your browser does not support the video tag.
              </video>
              <p class="text-muted mt-2">
                Video ID: {{ previewItem.content || previewItem.creative_id }}
              </p>
            </div>
            <div v-else>
              <feather-icon
                icon="PlayIcon"
                size="48"
                class="text-primary mb-2"
              />
              <p class="text-muted">
                Video Preview
              </p>
              <small class="text-muted">
                Video ID: {{ previewItem.content || previewItem.creative_id }}
              </small>
              <div class="mt-3">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="openVideoInNewTab(previewItem)"
                >
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="14"
                    class="mr-1"
                  />
                  Open Video
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Creative Details -->
      <div class="creative-details">
        <h6 class="mb-3">
          Creative Details
        </h6>
        <b-row>
          <b-col md="6">
            <div class="mb-2">
              <strong>Creative ID:</strong>
              <span class="ml-2">{{ isTextMaterial(previewItem) ? getTextMaterialContent(previewItem) : previewItem.creative_id }}</span>
            </div>
            <div class="mb-2">
              <strong>Type:</strong>
              <span class="ml-2 text-capitalize">{{ previewItem.material_type || previewItem.type }}</span>
            </div>
            <div class="mb-2">
              <strong>Status:</strong>
              <b-badge
                :variant="getTaskStatusVariant(previewItem.pre_review_status || previewItem.status)"
                class="ml-2"
              >
                <feather-icon
                  :icon="getTaskStatusIcon(previewItem.pre_review_status || previewItem.status)"
                  size="12"
                  class="mr-1"
                />
                {{ getTaskStatusText(previewItem.pre_review_status || previewItem.status) }}
              </b-badge>
            </div>
          </b-col>
          <b-col md="6">
            <div
              v-if="previewItem.reason"
              class="mb-2"
            >
              <strong>Review Reason:</strong>
              <p class="mt-1 text-muted small">
                {{ previewItem.reason }}
              </p>
            </div>
            <div
              v-if="previewItem.confidence_score"
              class="mb-2"
            >
              <strong>Confidence Score:</strong>
              <span class="ml-2">{{ Math.round(previewItem.confidence_score * 100) }}%</span>
            </div>
            <div
              v-if="previewItem.reviewed_at"
              class="mb-2"
            >
              <strong>Reviewed:</strong>
              <span class="ml-2">{{ formatDateTime(previewItem.reviewed_at) }}</span>
            </div>
          </b-col>
        </b-row>

        <!-- Reject Reasons -->
        <div
          v-if="previewItem.reject_info_list && previewItem.reject_info_list.length"
          class="mt-3"
        >
          <strong>Issues Found:</strong>
          <ul class="mt-2">
            <li
              v-for="(reason, idx) in previewItem.reject_info_list"
              :key="idx"
              class="text-danger small"
            >
              {{ reason }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BBadge,
  BImg,
  BButton,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'CreativePreviewModal',
  components: {
    BModal,
    BRow,
    BCol,
    BBadge,
    BImg,
    BButton,
    FeatherIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    previewItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      modalVisible: false,
    }
  },
  watch: {
    show: {
      handler(newVal) {
        this.modalVisible = newVal
      },
      immediate: true,
    },
    modalVisible(newVal) {
      this.$emit('input', newVal)
    },
  },
  methods: {
    getTaskStatusText(status) {
      const statusMap = {
        pending: 'Pending',
        processing: 'In Progress',
        completed: 'Completed',
        approved: 'Approved',
        rejected: 'Rejected',
        failed: 'Failed',
        // Pre-review material statuses
        APPROVED: 'Approved',
        REJECTED: 'Rejected',
        UNSURE: 'Unsure',
        UNAVAILABLE: 'Unavailable',
        // Lowercase versions
        unsure: 'Unsure',
        unavailable: 'Unavailable',
      }
      return statusMap[status] || status
    },
    getTaskStatusVariant(status) {
      const variantMap = {
        pending: 'light-warning',
        processing: 'light-info',
        completed: 'light-success',
        approved: 'light-success',
        rejected: 'light-danger',
        failed: 'light-danger',
        // Pre-review material statuses
        APPROVED: 'light-success',
        REJECTED: 'light-danger',
        UNSURE: 'light-warning',
        UNAVAILABLE: 'light-secondary',
        // Lowercase versions
        unsure: 'light-warning',
        unavailable: 'light-secondary',
      }
      return variantMap[status] || 'light-secondary'
    },
    getTaskStatusIcon(status) {
      const iconMap = {
        pending: 'ClockIcon',
        processing: 'RefreshCwIcon',
        completed: 'CheckCircleIcon',
        approved: 'CheckCircleIcon',
        rejected: 'XCircleIcon',
        failed: 'AlertTriangleIcon',
        // Pre-review material statuses
        APPROVED: 'CheckCircleIcon',
        REJECTED: 'XCircleIcon',
        UNSURE: 'HelpCircleIcon',
        UNAVAILABLE: 'MinusCircleIcon',
        // Lowercase versions
        unsure: 'HelpCircleIcon',
        unavailable: 'MinusCircleIcon',
      }
      return iconMap[status] || 'HelpCircleIcon'
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },
    isVideoMaterial(material) {
      if (!material) return false

      // Check material type
      const materialType = material.material_type || material.type || ''
      if (materialType.toLowerCase().includes('video')) {
        return true
      }

      // Check file extension from URL or content
      const contentUrl = material.content || material.url || material.creative_id || ''
      const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv']
      return videoExtensions.some(ext => contentUrl.toLowerCase().includes(ext))
    },
    getCreativeThumbnailUrl(material) {
      if (!material) return ''

      // Only process IMAGE materials
      if (material.type !== 'IMAGE' && material.material_type !== 'IMAGE') {
        return ''
      }

      // If content is a direct URL (from TikTok CDN), use it directly
      if (material.content && (material.content.startsWith('http://') || material.content.startsWith('https://'))) {
        return material.content
      }

      // For numeric creative IDs, use the API endpoint
      const creativeId = material.creative_id
      if (creativeId && /^\d+$/.test(creativeId.toString())) {
        const apiUrl = `/api/creative/image/${creativeId}`
        return apiUrl
      }

      return ''
    },
    handleImageError(event) {
      // Replace with a simple placeholder instead of hiding
      const placeholderUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iOCIgZmlsbD0iI0Y4RjlGQSIgc3Ryb2tlPSIjRTJFOEYwIiBzdHJva2Utd2lkdGg9IjIiLz4KPGRhdGggZD0iTTE2IDQwTDI0IDMyTDMyIDQwTDQ4IDI0VjQ4SDE2VjQwWiIgZmlsbD0iIzlDQTNBRiIvPgo8Y2lyY2xlIGN4PSIyNCIgY3k9IjI0IiByPSI0IiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo='
      const { target } = event
      target.src = placeholderUrl
      target.alt = 'Image not available'
      target.title = 'Image temporarily unavailable (CDN restriction)'
    },
    isTextMaterial(material) {
      if (!material) return false
      const materialType = (material.material_type || material.type || '').toUpperCase()
      return materialType === 'AD_TEXT' || materialType === 'LANDING_PAGE_URL'
    },
    getTextMaterialIcon(material) {
      const materialType = (material.material_type || material.type || '').toUpperCase()
      if (materialType === 'AD_TEXT') {
        return 'TypeIcon'
      }
      if (materialType === 'LANDING_PAGE_URL') {
        return 'LinkIcon'
      }
      return 'FileTextIcon'
    },
    getTextMaterialTitle(material) {
      const materialType = (material.material_type || material.type || '').toUpperCase()
      if (materialType === 'AD_TEXT') {
        return 'Ad Text Content'
      }
      if (materialType === 'LANDING_PAGE_URL') {
        return 'Landing Page URL'
      }
      return 'Text Content'
    },
    getTextMaterialContent(material) {
      // For text materials, the content should contain the actual text/URL
      // If content is the material ID (like material_2), try to get the actual content
      if (material.content && !material.content.startsWith('material_')) {
        return material.content
      }
      // Fallback to creative_id if content is not available or is a material ID
      if (material.creative_id && !material.creative_id.startsWith('material_')) {
        return material.creative_id
      }
      // If both are material IDs, show a placeholder message
      return 'Content not available'
    },
    getVideoUrl(material) {
      if (!material) return ''

      // Check if material is a video type
      if (!this.isVideoMaterial(material)) return ''

      // If content is a direct URL (from TikTok CDN), use it directly
      if (material.content && (material.content.startsWith('http://') || material.content.startsWith('https://'))) {
        return material.content
      }

      // Check for preview_url in metadata
      if (material.metadata?.preview_url) {
        return material.metadata.preview_url
      }

      // For TikTok video IDs (like v10033g50000d3tjmmnog65u8c23c3qg), use the API endpoint
      const videoId = material.content || material.creative_id || material.id
      if (videoId && videoId.startsWith('v')) {
        // Use the backend API endpoint that should fetch the video from TikTok
        return `/api/creative/video/${videoId}`
      }

      // For numeric creative IDs
      if (videoId && /^\d+$/.test(videoId.toString())) {
        return `/api/creative/video/${videoId}`
      }

      return ''
    },
    openVideoInNewTab(material) {
      const videoUrl = this.getVideoUrl(material)
      if (videoUrl) {
        window.open(videoUrl, '_blank')
      } else {
        // If no direct URL, try to construct a TikTok URL or use the creative ID
        const videoId = material.content || material.creative_id || material.id
        if (videoId) {
          // Try opening the API endpoint directly
          window.open(`/api/creative/video/${videoId}`, '_blank')
        }
      }
    },
  },
}
</script>

<style scoped>
.creative-preview-image {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.preview-placeholder {
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.video-preview-container {
  text-align: center;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}
</style>
